import { Breadcrumbs, Box, Divider, Link, Paper, SxProps, Theme, Typography, useTheme } from '@mui/material'
import React, { FC, ReactElement, ReactNode } from 'react'
import { Flex } from '../../Flex/flex.component';
import { Button } from '../../Form/Button/button.scomponent';
import { FaArrowCircleLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ListSearchField } from '../../List/list-search-field.component';
import { IListSearchPayload } from '../../List/list.types';

interface IBreadCrumbItem {
    title: string;
    link?: string;
}


interface IBaseContentPageProps {
    children: ReactNode;
    breadcrumb?: IBreadCrumbItem[];
    title: string;
    subtitle?: string;
    Action?: () => ReactElement;
    backButton?: boolean
    backUrl?:string
    titleBoxStyle?: object
    sx?: SxProps<Theme>;
    search?: boolean
    searchFunction?: (data: IListSearchPayload) => void
    searchDefaultValue?: string
    placeholderSearch?: string
    searchLoading?: boolean
    toolbarActions?: () => ReactElement
    breadcrumbStyle?: object;
    minHeight?: string
}


const BaseContentPage: FC<IBaseContentPageProps> = ({ children, breadcrumb, title, subtitle, Action, backButton = false, backUrl, titleBoxStyle = {}, search = false, searchFunction, searchDefaultValue, placeholderSearch, searchLoading = false, toolbarActions, breadcrumbStyle, minHeight,  ...rest }) => {
    const theme = useTheme();
    const navigate = useNavigate()
    return (
       <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '24px', minHeight: minHeight || '600px', ...rest}}>
            <Flex
              sx={{
                width: '100%',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
              }}
            >
                <Flex sx={{ alignItems: 'center', minWidth: 0, flex: 1, ...titleBoxStyle}}>
                    {backButton && <Button variant='text' sx={{ width: '30px', marginTop: '5px', marginLeft: '-18px'}} onClick={() => navigate(String(backUrl))}><FaArrowCircleLeft size={25} /></Button>}
                    <Flex sx={{ flexDirection: 'column', minWidth: 0 }}>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: subtitle ? 0 : '5px' }}>{title}</Typography>
                      {subtitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {subtitle}
                        </Typography>
                      )}
                    </Flex>
                </Flex>

                {Action && (
                  <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                    <Action />
                  </Box>
                )}
            </Flex>
            
            {breadcrumb?.length && (
                <Breadcrumbs
                  aria-label="breadcrumb"
                  sx={{ marginTop: 1, flexWrap: 'wrap', ...breadcrumbStyle }}
                >
                    {breadcrumb.map((item) => (
                        item.link ? (
                            <Link
                                underline="hover"
                                color="inherit"
                                href={item.link}
                                sx={{ color: theme.palette.primary.dark, fontSize: '0.875rem', fontWeight: 500 }}
                                key={item.title}
                            >
                                {item.title}
                            </Link>
                        ) : (
                            <Typography
                                sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem', fontWeight: 500 }}
                                key={item.title}
                            >
                                {item.title}
                            </Typography>
                        )
                    ))}
                </Breadcrumbs>
            )}

            {breadcrumb?.length && <Divider sx={{ marginTop: '10px', marginBottom: '20px' }} /> }

            {(search && searchFunction) || toolbarActions ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: 1.5,
                  mb: 2,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                {search && searchFunction && (
                  <ListSearchField
                    sx={{ flex: 1, minWidth: { md: 240 }, maxWidth: { md: 'none' } }}
                    placeholder={placeholderSearch || 'Pesquisar'}
                    defaultValue={searchDefaultValue}
                    onSearch={searchFunction}
                    loading={searchLoading}
                  />
                )}
                {toolbarActions && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      flexShrink: 0,
                      flexWrap: 'nowrap',
                      width: { xs: '100%', md: 'auto' },
                    }}
                  >
                    {toolbarActions()}
                  </Box>
                )}
              </Box>
            ) : null}
            
            {children}
       </Paper>
    );
};

export { BaseContentPage  }
